import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../types';
import { fetchProduct } from '../utils/api';
import { PLACEHOLDER_IMG } from '../utils/constants';
import { useCart } from '../contexts/CartContext';
import EnquiryForm from '../components/EnquiryForm';
import SEOHead from '../components/SEOHead';
import './Cart.css';

const Cart: React.FC = () => {
  const { lines, updateQty, removeFromCart, clearCart } = useCart();
  const [products, setProducts] = useState<Map<number, Product>>(new Map());
  const [loading,  setLoading]  = useState<boolean>(true);
  const [sent,     setSent]     = useState<boolean>(false);

  // Refetch only when the *set* of ids changes (not on qty +/- edits) —
  // this key is stable across quantity-only updates to the same lines.
  const lineIdsKey = lines.map(l => l.id).join(',');

  useEffect(() => {
    if (lines.length === 0) { setProducts(new Map()); setLoading(false); return; }
    setLoading(true);
    Promise.allSettled(lines.map(l => fetchProduct(l.id)))
      .then(results => {
        const map = new Map<number, Product>();
        const missingIds: number[] = [];
        results.forEach((r, i) => {
          if (r.status === 'fulfilled') map.set(lines[i].id, r.value.product);
          else missingIds.push(lines[i].id);
        });
        missingIds.forEach(removeFromCart); // clean up items that no longer exist
        setProducts(map);
      })
      .finally(() => setLoading(false));
    // Deliberately keyed on lineIdsKey (the id set), not `lines` — a qty-only
    // change on an existing line shouldn't trigger a refetch.
  }, [lineIdsKey]);

  const items = lines
    .map(l => ({ line: l, product: products.get(l.id) }))
    .filter((x): x is { line: typeof lines[0]; product: Product } => !!x.product);

  const total = items.reduce((sum, { line, product }) => sum + product.price * line.qty, 0);

  if (sent) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="empty-state">
            <div className="empty-icon">✅</div>
            <h3>Enquiry Sent!</h3>
            <p>We'll get back to you shortly about these items. Thank you!</p>
            <Link to="/products" className="btn-primary" style={{ marginTop: '16px', display: 'inline-block' }}>
              Continue Browsing
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <SEOHead title="My Basket" description="Products you've added to your basket at Balaji Fashion House." url="/cart" />
      <div className="container">
        <div className="page-heading">
          <h1 className="section-title">My Basket</h1>
          <p className="section-subtitle">
            {items.length > 0 ? `${items.length} item${items.length > 1 ? 's' : ''} — send one enquiry for all of them` : 'Nothing added yet'}
          </p>
        </div>

        {loading ? (
          <div className="loader"><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🧺</div>
            <h3>Your basket is empty</h3>
            <p>Tap the basket icon on any product to add it here, then send one enquiry for everything at once.</p>
            <Link to="/products" className="btn-primary" style={{ marginTop: '16px', display: 'inline-block' }}>
              Browse Collections
            </Link>
          </div>
        ) : (
          <div className="cart-layout">
            <div className="cart-lines">
              {items.map(({ line, product }) => (
                <div key={product.id} className="cart-line">
                  <Link to={`/product/${product.id}`} className="cart-line-img">
                    <img src={product.image} alt={product.name}
                         onError={e => { e.currentTarget.src = PLACEHOLDER_IMG; }} />
                  </Link>
                  <div className="cart-line-info">
                    <Link to={`/product/${product.id}`} className="cart-line-name">{product.name}</Link>
                    <span className="badge badge-category">{product.category}</span>
                    <div className="cart-line-price">₹{product.price.toLocaleString('en-IN')}</div>
                  </div>
                  <div className="cart-line-qty">
                    <button type="button" onClick={() => updateQty(product.id, line.qty - 1)} aria-label="Decrease quantity">−</button>
                    <span>{line.qty}</span>
                    <button type="button" onClick={() => updateQty(product.id, line.qty + 1)} aria-label="Increase quantity">+</button>
                  </div>
                  <div className="cart-line-subtotal">₹{(product.price * line.qty).toLocaleString('en-IN')}</div>
                  <button type="button" className="cart-line-remove" onClick={() => removeFromCart(product.id)} aria-label="Remove item">
                    <i className="fa-solid fa-trash-can" aria-hidden="true" />
                  </button>
                </div>
              ))}
              <button type="button" className="link-btn cart-clear-all" onClick={clearCart}>Clear basket</button>
            </div>

            <div className="cart-summary">
              <div className="cart-total-row">
                <span>Estimated Total</span>
                <b>₹{total.toLocaleString('en-IN')}</b>
              </div>
              <p className="cart-note">This is a WhatsApp/email enquiry, not a checkout — no payment is taken here. We'll confirm final pricing and availability with you directly.</p>
              <h2 className="cart-enquiry-heading">Send Enquiry for All Items</h2>
              <EnquiryForm
                products={items.map(({ line, product }) => ({ product, qty: line.qty }))}
                onSuccess={() => { clearCart(); setSent(true); }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
